import { useEffect, useRef, useState } from 'react';
import FlexColumn from '../../components/FlexColumn/FlexColumn.js';
import * as pdfjsLib from 'pdfjs-dist/webpack';
import './PdfPreviewer.css';

function base64ToUint8Array(base64) {
	const cleanedBase64 = base64.replace(/[^A-Za-z0-9+/=]/g, "");
	const raw = window.atob(cleanedBase64);
	const uint8Array = new Uint8Array(raw.length);
	for (let i = 0; i < raw.length; i++) {
		uint8Array[i] = raw.charCodeAt(i);
	}
	return uint8Array;
}

function newBorderDiv() {
	const nullDiv = document.createElement("div");
	nullDiv.style.width = "140px";
	nullDiv.style.height = "181px";
	nullDiv.style.outline = "2px solid #888";
	nullDiv.style.color = "#888";
	nullDiv.style.display = "flex";
	nullDiv.style.alignItems = "center";
	nullDiv.style.justifyContent = "center";
	return nullDiv;
}

const PdfPreviewer = ({ base64Data, loading, parishName, bulletinsLink }) => {
	const thumbnailRef = useRef(null);
	const [selectedPdf, setSelectedPdf] = useState(false);
	const overlayRef = useRef(null);

	// Render thumbnail (first page only)
	//====================================
	useEffect(() => {
		if (!thumbnailRef.current) return;

		const renderThumbnail = async () => {
			thumbnailRef.current.innerHTML = "";

			if (loading === true && !base64Data) {
				const nullDiv = newBorderDiv();
				nullDiv.innerHTML = "<span class='pdf-loader'></span>";
				thumbnailRef.current.appendChild(nullDiv);
			} else if (loading === false && !base64Data) {
				const nullDiv = newBorderDiv();
				nullDiv.innerHTML = "<p>Failed to load</p>";
				thumbnailRef.current.appendChild(nullDiv);
			} else {
				const pdfData = base64ToUint8Array(base64Data);
				const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;
				const page = await pdf.getPage(1);
				const viewport = page.getViewport({ scale: 1 });

				const desiredWidth = 140;
				const scale = desiredWidth / viewport.width;

				const canvas = document.createElement("canvas");
				const context = canvas.getContext("2d");
				canvas.width = desiredWidth;
				canvas.height = viewport.height * scale;

				if (thumbnailRef.current && thumbnailRef.current.children.length === 0) {
					// React's local double render is annoying in this case
					thumbnailRef.current.appendChild(canvas);
				}

				await page.render({ canvasContext: context, viewport: page.getViewport({ scale }) }).promise;
			}
		};

		renderThumbnail();
	}, [base64Data, loading]);

	// Render full PDF in overlay
	//============================
	useEffect(() => {
		const container = overlayRef.current;
		if (!selectedPdf || !container) return;

		const renderFullPdf = async () => {
			container.innerHTML = "";
			const pdfData = base64ToUint8Array(base64Data);
			const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;
			const deviceScale = window.devicePixelRatio || 1;

			for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
				const page = await pdf.getPage(pageNum);
				// Fit pages inside screen
				const pageViewport = page.getViewport({ scale: 1 });
				const containerWidth = container.clientWidth;
				const screenFitScale = Math.min(1.5, containerWidth / pageViewport.width);
				const scaledViewport = page.getViewport({ scale: screenFitScale });

				const canvas = document.createElement("canvas");
				const context = canvas.getContext("2d");
				// Paint canvas with High-DPI
				canvas.width = scaledViewport.width * deviceScale;
				canvas.height = scaledViewport.height * deviceScale;
				// Resize canvas content to fit screen
				canvas.style.width = `${scaledViewport.width}px`;
				canvas.style.height = `${scaledViewport.height}px`;
				canvas.style.marginBottom = "20px"; // spacing between pages
				context.setTransform(deviceScale, 0, 0, deviceScale, 0, 0);

				if (!selectedPdf || !container) return; // user may have closed it already
				container.appendChild(canvas);

				await page.render({ canvasContext: context, viewport: scaledViewport }).promise;
			}
		};

		renderFullPdf();
	}, [selectedPdf, base64Data]);

	const handlePdfClick = (pdfBase64) => {
		if (pdfBase64) {
			setSelectedPdf(true);
		}
	};

	const closeOverlay = () => {
		setSelectedPdf(false);
	};

	return (
		<div>
			<FlexColumn gap="0px">
				<div className='minipdf' onClick={() => handlePdfClick(base64Data)} ref={thumbnailRef}></div>
				<h3>{parishName}</h3>
				<a target='_blank' rel="noreferrer" href={bulletinsLink}>See other bulletins...</a>
			</FlexColumn>
			{selectedPdf && (
				<div className="previewOverlay" onClick={closeOverlay}>
					<div className="overlayContent" onClick={(e) => e.stopPropagation()}>
						<div ref={overlayRef}></div>
					</div>
				</div>
			)}
		</div>
	);
};

export default PdfPreviewer;
